import { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

// ---------------------------------------------------------

import Icon from '@components/icon';
import Tooltip from '@components/tooltip';

// ---------------------------------------------------------

// Import formatting utilities for calculator inputs
let formatCurrencyInput, extractNumericValue;
try {
  const utils = require('@components/calculators/shared/calculator-utils');
  formatCurrencyInput = utils.formatCurrencyInput;
  extractNumericValue = utils.extractNumericValue;
} catch {
  // Fallback if calculator utilities not available
  formatCurrencyInput = (input) => ({
    displayValue: input,
    numericValue: parseFloat(input) || 0,
    cursorPosition: 0
  });
  extractNumericValue = (input) => parseFloat(String(input).replace(/[^0-9.]/g, '')) || 0;
}

// ---------------------------------------------------------

import styles from '../styles.module.scss';
const {
  input_container,
  input_field,
  input_icon,
  input_label,
  label_with_tooltip,
  info_icon,
  error,
  has_error,
  percent_symbol,
  with_percent
} = styles;

// ---------------------------------------------------------

const InputField = (props) => {
  const {
    icon,
    placeholder,
    label,
    tooltip,
    searchValueCallback,
    inputType = 'text',
    inputFormat, // 'currency' or 'percentage'
    inputName,
    pattern,
    customError,
    required = false,
    defaultInputValue,
    defaultOnChange,
    defaultOnBlur
  } = props;

  const typeMap = {
    tel: {
      customError: 'Please enter a valid phone number. Ex: 123-123-1234',
      pattern: '^(\\+\\d{1,2}\\s)?\\(?\\d{3}\\)?[\\s.-]?\\d{3}[\\s.-]?\\d{4}$'
    }
  };

  const inputRef = useRef(null);
  const errorMsg =
    inputRef?.current?.validationMessage &&
    (customError || typeMap[inputType]?.customError || inputRef?.current?.validationMessage);
  const [inputValue, setInputValue] = useState(defaultInputValue || '');
  const [showError, setShowError] = useState(false);

  // Initialize with formatted default value if format is specified
  useEffect(() => {
    if (defaultInputValue && inputFormat) {
      let formatted;
      if (inputFormat === 'currency') {
        formatted = formatCurrencyInput(defaultInputValue);
        setInputValue(formatted.displayValue);
      } else if (inputFormat === 'percentage') {
        // Just show the number without the % symbol
        setInputValue(defaultInputValue.toString());
      }
    } else {
      setInputValue(defaultInputValue || '');
    }
  }, [defaultInputValue, inputFormat]);

  const handleInputChange = (e) => {
    const rawValue = e.target.value;
    const cursorPosition = e.target.selectionStart;
    let finalValue = rawValue;
    let numericValue = rawValue;

    // Apply formatting if specified
    if (inputFormat === 'currency') {
      const formatted = formatCurrencyInput(rawValue, cursorPosition);
      finalValue = formatted.displayValue;
      numericValue = formatted.numericValue;

      // Set cursor position after state update
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.setSelectionRange(formatted.cursorPosition, formatted.cursorPosition);
        }
      }, 0);
    } else if (inputFormat === 'percentage') {
      // Remove any non-numeric characters except decimal point
      const cleanValue = rawValue.replace(/[^0-9.]/g, '');

      // Ensure only one decimal point and max 2 decimal places
      const parts = cleanValue.split('.');
      let cleanNumber = parts[0] || '';
      if (parts.length > 1) {
        cleanNumber += '.' + parts[1].slice(0, 2);
      }

      // Convert to number and cap at 100
      numericValue = Math.min(100, parseFloat(cleanNumber) || 0);
      finalValue = cleanNumber;
    } else {
      numericValue = extractNumericValue(rawValue);
    }

    setInputValue(finalValue);

    // Call callbacks with appropriate values
    if (searchValueCallback) searchValueCallback(finalValue);
    if (defaultOnChange) {
      // For formatted inputs, pass the numeric value for calculations
      defaultOnChange(inputFormat ? numericValue : finalValue);
    }
  };

  // ---------------------------------------------------------

  return (
    <div className={input_container}>
      <label className={input_label}>
        {label && (
          <div className={label_with_tooltip}>
            <span style={{ marginBottom: 0 }}>{label}</span>
            {tooltip && (
              <Tooltip content={tooltip} position="top">
                <div className={info_icon}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="16" x2="12" y2="12" />
                    <circle cx="12" cy="8" r="0.5" fill="currentColor" />
                  </svg>
                </div>
              </Tooltip>
            )}
          </div>
        )}
        <div style={{ position: 'relative' }}>
          <input
            ref={inputRef}
            type={inputType}
            className={classNames(input_field, {
              [has_error]: errorMsg && showError,
              [with_percent]: inputFormat === 'percentage'
            })}
            placeholder={placeholder !== undefined ? placeholder : typeMap[inputType]?.placeholder}
            pattern={pattern || typeMap[inputType]?.pattern}
            onChange={handleInputChange}
            onFocus={() => setShowError(false)}
            onBlur={() => {
              setShowError(true);
              if (defaultOnBlur) {
                // Get the current numeric value for the callback
                const numericValue = inputFormat ? extractNumericValue(inputValue) : inputValue;
                defaultOnBlur(numericValue);
              }
            }}
            value={inputValue}
            name={inputName}
            required={required}
          />
          {inputFormat === 'percentage' && <span className={percent_symbol}>%</span>}
        </div>
        {errorMsg && showError && <div className={error}>{errorMsg}</div>}
      </label>
      {icon && (
        <div className={input_icon}>
          <Icon type={icon?.iconType} name={icon?.iconName} />
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------

InputField.propTypes = {
  customError: PropTypes.string,

  /**
   * Specifies the default value
   */
  defaultInputValue: PropTypes.string,
  /**
   * Specifies the onChange function that sets the default value
   */
  defaultOnChange: PropTypes.func,
  /**
   * Specifies the onBlur function called when input loses focus
   */
  defaultOnBlur: PropTypes.func,

  icon: PropTypes.object,

  /**
   * Specifies the input formatting type ('currency' or 'percentage')
   */
  inputFormat: PropTypes.oneOf(['currency', 'percentage']),

  inputName: PropTypes.string,

  inputType: PropTypes.oneOf(['email', 'tel', 'text', 'date', 'time', 'number', 'search']),

  label: PropTypes.string,

  pattern: PropTypes.string,

  placeholder: PropTypes.string,

  required: PropTypes.bool,

  searchValueCallback: PropTypes.func,

  /**
   * Tooltip content to display next to the label
   */
  tooltip: PropTypes.node
};

export default InputField;
